import { toEntries } from '@spazious/dollhouse-utils';

import { ScopeImpl } from '../scope/index.js';

import { SchemaField, SchemaFieldOptions } from './SchemaField.js';
import { SchemaRulesEngine } from './SchemaRulesEngine.js';
import {
  Schema,
  SchemaEvaluatedField,
  SchemaEvaluatedFieldValues,
  SchemaValues,
  SchemaVariables,
} from './types.js';
import { clampFieldValue, isSchemaField } from './utils.js';

export interface SchemaCompositionOptions extends SchemaFieldOptions {}

export class SchemaImpl<
  TSchema extends Schema = Schema,
  TVariables extends SchemaVariables = {}
> extends ScopeImpl<SchemaValues<TSchema['fields']>> {
  public schema: TSchema;
  public schemaFieldKeys: Set<string>;
  public globalKeys: Set<string>;
  public cache: Map<string, any> = new Map();
  public rulesEngine: SchemaRulesEngine<TSchema, TVariables>;

  public options: SchemaCompositionOptions;

  constructor(schema: TSchema, initialValues?: TVariables, opts?: SchemaCompositionOptions) {
    super();
    this.schema = schema;
    this.options = { ...opts };
    this.schemaFieldKeys = new Set(Object.keys(schema['fields']));
    this.globalKeys = new Set(Object.keys(initialValues ?? {}));
    this.rulesEngine = new SchemaRulesEngine<TSchema, TVariables>(this, schema.rules ?? []);
    this.set(schema['fields']);
    this.setValues(initialValues ?? {});
  }

  /**
   * Gets a variable value if exists or field schema definition
   */
  get<TKey extends keyof (SchemaValues<TSchema['fields']> & TVariables)>(
    key: TKey,
    bubble = true
  ): (SchemaValues<TSchema['fields']> & TVariables)[TKey] {
    return super.get(key as string, bubble);
  }

  /**
   * Checks if a variable exists or field schema
   */
  has(key: keyof (SchemaValues<TSchema['fields']> & TVariables)): boolean {
    return super.has(key as string);
  }

  getField<TKey extends keyof TSchema['fields'], TEvaluate extends boolean = true>(
    key: TKey,
    evaluated = true as TEvaluate
  ): TEvaluate extends true
    ? SchemaEvaluatedFieldValues<TSchema['fields'][TKey]>
    : TSchema['fields'][TKey] {
    return super.get(key as string).toJSON(evaluated);
  }

  parseValue(key: string, value: any) {
    // If the value is an object, then assume is a scope
    // with type definition and create a new scope
    return isSchemaField(value) ? new SchemaField(value, key, this, this.options) : value;
  }

  *entries(evaluated: boolean = true): IterableIterator<[string, any]> {
    const iter = this.schemaFieldKeys.keys();
    let result = iter.next();

    while (!result.done) {
      const key = result.value as string;
      const value = super.get(key);
      yield [key, evaluated ? value.toJSON(evaluated) : value.toJSON()];
      result = iter.next();
    }
  }

  *keys(): IterableIterator<keyof TSchema['fields'] | keyof TVariables> {
    yield* super.keys();
  }

  *values(): IterableIterator<[string, any]> {
    const iter = this.keys();
    let result = iter.next();

    while (!result.done) {
      const key = result.value as string;
      yield [key, this.getValue(key)];
      result = iter.next();
    }
  }

  toJSON(): SchemaEvaluatedField {
    return Object.fromEntries(this.entries()) as any;
  }

  getValue(key: string, clamped: boolean = true) {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    if (super.has(key, false)) {
      const field = super.get(key, false);
      if (field instanceof ScopeImpl && field.has('value')) {
        let value = field.get('value');
        if (this.schemaFieldKeys.has(key) && clamped) {
          value = clampFieldValue(this.getField(key, true), value);
        }
        this.cache.set(key, value);
        return value;
      } else {
        return field;
      }
    }

    throw new Error(`Symbol "${key}" not found on this schema`);
  }

  setValue(key: string, value: any) {
    if (this.has(key)) {
      let target: any = this;
      let targetKey = key;
      const field = super.get(key, false);

      if (field instanceof SchemaField && field.has('value')) {
        target = field;
        targetKey = 'value';
        // const oldValue = target.get(targetKey);
        // if (oldValue !== value) {
        //   this.rulesEngine.emit('change', field as any);
        // }
      }
      if (value === undefined) {
        target.delete(targetKey);
      } else {
        target.set(targetKey, value);
      }
    } else {
      this.set(key, value);
    }

    if (this.cache.size) {
      this.cache.clear();
    }
    return this;
  }

  /**
   * Overwrite all the values.
   * If any of the schemaFieldKeys is missing from values, it is reset to default value.
   */
  setValues(values: Partial<SchemaValues<TSchema['fields']> & TVariables>) {
    const alreadyKeys = new Set<string>();

    toEntries(values as any).forEach(([key, value]) => {
      this.setValue(key as string, value);
      alreadyKeys.add(key as string);
    });

    // Update the fields defined in the schema to their default values
    this.schemaFieldKeys.forEach((key) => {
      if (!alreadyKeys.has(key) && !this.globalKeys.has(key)) {
        this.setValue(key, values[key]);
      }
    });

    return this;
  }

  updateValues(values: Partial<SchemaValues<TSchema['fields']> & TVariables>) {
    toEntries(values as any).forEach(([key, value]) => {
      this.setValue(key as string, value);
    });

    return this;
  }

  getValues(): SchemaValues<TSchema['fields']> & TVariables {
    return Object.fromEntries(this.values()) as any;
  }

  clone(): SchemaImpl<TSchema, TVariables> {
    const clone = new SchemaImpl<TSchema, TVariables>(this.schema, this.getValues(), this.options);
    clone.rulesEngine = this.rulesEngine;
    return clone;
  }
}
